import { extendObservable, runInAction } from 'mobx';

import { PasswordConfirmationStore } from '../stores/PasswordConfirmationStore';
import { BillingDataController } from '../networking/controllers/BillingDataContoller';
import { ApiError, ERROR_STATUSES, ERROR_CODES } from '../models/ApiError';
import { Logger } from '../helpers/Logger';

class BillingStoreClass {
  constructor() {
    extendObservable(this, {
      billingData: null,
      loadingBillingData: false,
      getError: false,
      submitError: false,
      submitApiError: null,
      submitInternalError: false,

      deletionError: false,
      deletionInternalError: false,
      deletionSuccess: false,
      deletingBillingData: false,
    });
  }

  async getBillingData() {
    try {
      const billingData = await BillingDataController.all();
      runInAction(() => {
        this.billingData = billingData;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.getError = true;
      });
    }
  }

  async createBillingData(type, data) {
    runInAction(() => {
      this.submitError = false;
      this.submitApiError = null;
      this.submitInternalError = false;
    });
    try {
      const billingData = await BillingDataController.create(type, data);
      runInAction(() => {
        this.billingData.push(billingData);
      });
      return billingData;
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          return await PasswordConfirmationStore.showConfirmationModal(() => (
            this.createBillingData(type, data)
          ));
        } catch (creationError) {
          runInAction(() => {
            this.submitError = creationError;
          });
        }
      } else if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.submitInternalError = true;
          });
        } else if (error.code === ERROR_CODES.INVALID_RUT ||
            error.code === ERROR_CODES.INVALID_URUGUAYAN_DOCUMENT) {
          runInAction(() => {
            this.submitApiError = error.code;
          });
        } else {
          runInAction(() => {
            this.submitError = true;
          });
        }
      }
      return null;
    }
  }

  async editBillingData(id, type, data) {
    runInAction(() => {
      this.submitError = false;
      this.submitApiError = null;
      this.submitInternalError = false;
    });
    try {
      const billingDataEdited = await BillingDataController.edit(id, type, data);
      runInAction(() => {
        const billingData = this.billingData.filter(billing => billing.id !== id);
        billingData.push(billingDataEdited);
        this.billingData = billingData;
      });
      return billingDataEdited;
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          return await PasswordConfirmationStore.showConfirmationModal(() => (
            this.editBillingData(id, type, data)
          ));
        } catch (editError) {
          runInAction(() => {
            this.submitError = editError;
          });
        }
      } else if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.submitInternalError = true;
          });
        } else if (error.code === ERROR_CODES.INVALID_RUT ||
            error.code === ERROR_CODES.INVALID_URUGUAYAN_DOCUMENT) {
          runInAction(() => {
            this.submitApiError = error.code;
          });
        } else {
          runInAction(() => {
            this.submitError = true;
          });
        }
      }
      return null;
    }
  }

  async deleteBillingData(id) {
    runInAction(() => {
      this.deletionError = false;
      this.deletingBillingData = true;
    });
    try {
      await BillingDataController.delete(id);
      const billingData = this.billingData.filter(billing => billing.id !== id);
      runInAction(() => {
        this.billingData = billingData;
        this.deletionSuccess = true;
      });
    } catch (error) {
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          await PasswordConfirmationStore.showConfirmationModal(() => (
            this.deleteBillingData(id)
          ));
        } catch (deletionError) {
          runInAction(() => {
            this.deletionError = deletionError;
          });
        }
      } else if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
        runInAction(() => {
          this.deletionInternalError = true;
        });
      } else {
        runInAction(() => {
          this.deletionError = true;
        });
      }
      Logger.warn(error);
    } finally {
      runInAction(() => {
        this.deletingBillingData = false;
      });
    }
  }

  async createBillingDataV2(type, data) {
    runInAction(() => {
      this.submitError = false;
      this.submitApiError = null;
      this.submitInternalError = false;
    });
    try {
      const billingData = await BillingDataController.createNewApi(type, data);
      runInAction(() => {
        this.billingData.push(billingData);
      });
      return billingData;
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.submitError = true;
      });
      return null;
    }
  }

  async editBillingDataV2(id, type, data) {
    runInAction(() => {
      this.submitError = false;
      this.submitApiError = null;
      this.submitInternalError = false;
    });
    try {
      console.log(data);
      const billingDataEdited = await BillingDataController.editNewApi(id, type, data);
      runInAction(() => {
        const billingData = this.billingData.filter(billing => billing.id !== id);
        billingData.push(billingDataEdited);
        this.billingData = billingData;
      });
      return billingDataEdited;
    } catch (error) {
      Logger.warn(error);
      if (error.response && error.response.status === ERROR_STATUSES.PRECONDITION_FAILED) {
        // Wait for confirmation and try to submit again when done
        try {
          return await PasswordConfirmationStore.showConfirmationModal(() => (
            this.editBillingData(id, type, data)
          ));
        } catch (editError) {
          runInAction(() => {
            this.submitError = editError;
          });
        }
      } else if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.submitInternalError = true;
          });
        } else if (error.code === ERROR_CODES.INVALID_RUT ||
            error.code === ERROR_CODES.INVALID_URUGUAYAN_DOCUMENT) {
          runInAction(() => {
            this.submitApiError = error.code;
          });
        } else {
          runInAction(() => {
            this.submitError = true;
          });
        }
      }
      return null;
    }
  }

  clearGetDataError() {
    runInAction(() => {
      this.getError = false;
    });
  }

  clearSubmitApiError() {
    runInAction(() => {
      this.submitApiError = false;
    });
  }

  clearDeletionErrors() {
    runInAction(() => {
      this.deletionError = false;
    });
  }

  clearDeletionSuccess() {
    runInAction(() => {
      this.deletionSuccess = false;
    });
  }

  resetErrors() {
    runInAction(() => {
      this.submitError = false;
      this.submitApiError = null;
      this.submitInternalError = false;
    });
  }
}

const BillingStore = new BillingStoreClass();

export { BillingStore };



// WEBPACK FOOTER //
// ./src/stores/BillingStore.js